import { useMemo, type ReactNode } from "react";
import { ErrorBoundary } from "@sentry/react";
import { useTranslation } from "react-i18next";
import { Button } from "@hulahoop/ui";

interface ShellBoundaryProps {
  message: string;
  className?: string;
  action?: ReactNode;
  children: ReactNode;
}

function ShellBoundaryFallback({
  message,
  className,
  action,
  onRetry,
}: Omit<ShellBoundaryProps, "children"> & { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div role="alert" className={className}>
      <p>{message}</p>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onRetry}>
          {t("shell.retry")}
        </Button>
        {action}
      </div>
    </div>
  );
}

function createFallback(props: Omit<ShellBoundaryProps, "children">) {
  return function ShellBoundaryFallbackRender({ resetError }: { resetError: () => void }) {
    return <ShellBoundaryFallback {...props} onRetry={resetError} />;
  };
}

export function ShellBoundary({ children, message, className, action }: ShellBoundaryProps) {
  const fallback = useMemo(
    () => createFallback({ message, className, action }),
    [message, className, action],
  );
  return <ErrorBoundary fallback={fallback}>{children}</ErrorBoundary>;
}
